import { SelectOption } from '@app/shared/models/option';
import styles from '../opm-summary-waterfall-erf.module.scss';
import { FormProvider, useForm } from 'react-hook-form';
import { formConfigBase } from '@app/shared/constants/form-config-base';
import { useAppDispatch, useAppSelector } from '@app/core/hooks/redux-hooks';
import { dateFormatter } from '@app/shared/formatters';
import { Navigate, generatePath } from 'react-router-dom';
import { RouteConstants } from '@app/modules/projects/RouteConstants';
import { cloneToMutableData } from '@app/shared/helpers';
import { setOpmWaterfallSelectedTab } from '@core/store/ui-values-slice';
import useSetSelectedTab from '@app/core/hooks/useSetSelectedTab';
import { CELL_WIDTH, TITLE_WIDTH } from './constants';
import SampleWaterfallInputs from './sample-waterfall-inputs/SampleWaterfallInputs';
import { ExitScenarioInputDto } from '@app/shared/models/contracts/project-dto';
import { useEffect, useMemo, useRef, useState } from 'react';
import { projectSlice } from '@app/core/store/project-slice';
import {
  calculateOpmSampleWaterfall,
  clearOpmSampleWaterfallResults,
} from '@app/core/store/opm-calculation-slice';
import SampleWaterfallOutputsSummary from './sample-waterfall-outputs/SampleWaterfallOutputsSummary';
import { OpmSampleWaterfallsDto } from '@app/shared/models/contracts/opm-calculation-results-dto';
import { getScenarioColumnKeys, getScenarioSignature, sortScenarioKeys } from './scenario-columns';

const SampleWaterfallTable = (): JSX.Element => {
  useSetSelectedTab(RouteConstants.SampleWaterfall, setOpmWaterfallSelectedTab);
  const dispatch = useAppDispatch();
  const project = useAppSelector((state) => state.project.projectDraft);
  const opmCalculation = useAppSelector((state) => state.opmCalculation);
  const opmWaterfall = opmCalculation?.riskFreeValues.waterfalls;
  const riskFreeResults = useAppSelector((state) => state.opmCalculation.riskFreeValues);
  const sampleWaterfallResults = riskFreeResults?.sampleWaterfall;
  // the project draft has changed since these results were calculated, so they no longer reflect it
  const areResultsStale = useAppSelector((state) => state.opmCalculation.isSampleWaterfallStale);

  let yearOptions: SelectOption[] = [];

  if (opmWaterfall && Object.keys(opmWaterfall).length > 0) {
    yearOptions = Object.keys(opmWaterfall).map((key) => ({
      value: key,
      viewValue: dateFormatter(key, { month: 'short', year: 'numeric' }),
    }));
  }

  // measure taken to prevent the exit calc api from failing if selectedYear
  // is removed, and the user re-runs the calc whilst viewing the table
  const yearOptionsValues = yearOptions.map((option) => option.value);

  const exitScenarios = project.opmInput.exitScenarios ?? ({} as Dictionary<ExitScenarioInputDto>);

  const firstInput = Object.values(exitScenarios)[0];

  // the values the results currently reflect, so that blurring a field without changing it
  // does not trigger another calculation
  const lastSubmittedSignature = useRef<string | null>(null);

  useEffect(() => {
    const hasStaleResults = Boolean(sampleWaterfallResults) && areResultsStale;

    if (hasStaleResults) {
      dispatch(clearOpmSampleWaterfallResults());
    }

    if (firstInput?.enterpriseValue && (!sampleWaterfallResults || hasStaleResults)) {
      submitData();
    } else {
      lastSubmittedSignature.current = getScenarioSignature(updatedExitScenarios);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updatedExitScenarios: Dictionary<ExitScenarioInputDto> = Object.entries(
    cloneToMutableData(exitScenarios)
  ).reduce((acc, [key, selectedValue]) => {
    if (selectedValue.enterpriseValue === 0) {
      selectedValue.enterpriseValue = null;
    }
    if (!yearOptionsValues.includes(selectedValue.exitDate)) {
      selectedValue.exitDate = yearOptions?.[0]?.value?.toString() ?? '';
    }
    acc[key] = selectedValue as ExitScenarioInputDto;
    return acc;
  }, {} as Dictionary<ExitScenarioInputDto>);

  const formMethods = useForm({
    ...formConfigBase,
    defaultValues: { exitScenarios: updatedExitScenarios },
  });

  const { handleSubmit, getValues, getFieldState, reset } = formMethods;

  // the scenario columns are driven by the project draft, so the form has to be re-seeded
  // whenever a scenario is added or removed
  const scenarioKeys = Object.keys(exitScenarios);
  const scenarioKeysSignature = scenarioKeys.join(',');
  const previousScenarioKeysSignature = useRef(scenarioKeysSignature);

  useEffect(() => {
    if (previousScenarioKeysSignature.current !== scenarioKeysSignature) {
      const previousKeys = previousScenarioKeysSignature.current.split(',').filter(Boolean);
      const scenarioWasAdded = scenarioKeys.length > previousKeys.length;
      previousScenarioKeysSignature.current = scenarioKeysSignature;
      reset({ exitScenarios: updatedExitScenarios });

      // an added scenario is a copy of the previous column, so it can be calculated immediately
      if (scenarioWasAdded) {
        submitData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioKeysSignature, reset, updatedExitScenarios]);

  // the calculation endpoint is given an array of scenarios and returns results keyed by that
  // array's position, so the results have to be mapped back onto the scenario (column) keys
  const [submittedColumnKeys, setSubmittedColumnKeys] = useState<string[]>([]);

  const columnKeys = useMemo(
    () => getScenarioColumnKeys(exitScenarios),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scenarioKeysSignature]
  );

  const waterfallResultsByColumn = useMemo(() => {
    const keysUsedForResults = submittedColumnKeys.length > 0 ? submittedColumnKeys : columnKeys;

    return keysUsedForResults.reduce((acc, columnKey, index) => {
      const result = sampleWaterfallResults?.[index];
      if (result) {
        acc[columnKey] = result;
      }
      return acc;
    }, {} as Dictionary<OpmSampleWaterfallsDto>);
  }, [sampleWaterfallResults, submittedColumnKeys, columnKeys]);

  // the IRR hurdle drill down navigates to the year of the scenario whose cell was clicked
  const watchedScenarios = formMethods.watch('exitScenarios');
  const exitDatesByColumn = columnKeys.reduce((acc, columnKey) => {
    acc[columnKey] = watchedScenarios?.[columnKey]?.exitDate ?? '';
    return acc;
  }, {} as Dictionary<string>);

  if (!riskFreeResults || Object.keys(riskFreeResults).length === 0) {
    const newPath = generatePath(`../../${RouteConstants.ProjectDetails}`);
    return <Navigate to={newPath} />;
  }

  const shouldDisplayOutputs =
    sampleWaterfallResults && Object.keys(sampleWaterfallResults).length > 0;

  const submitData = async () => {
    const { exitScenarios } = getValues();

    // Build a dictionary of only valid entries
    const validEntries = sortScenarioKeys(exitScenarios).reduce((acc, key) => {
      const value = exitScenarios[key];
      const fieldState = getFieldState(`exitScenarios.${key}.enterpriseValue`);
      if (!fieldState.invalid) {
        if (!value.exitDate) {
          value.exitDate = yearOptions?.[0]?.value?.toString() ?? '';
        }
        acc[key] = value;
      }
      return acc;
    }, {} as Dictionary<ExitScenarioInputDto>);

    // If there are no valid entries, stop
    if (Object.keys(validEntries).length === 0) {
      return;
    }

    const submittedKeys = sortScenarioKeys(validEntries);
    const validEntriesArray = submittedKeys.map((key) => ({ ...validEntries[key] }));

    // nothing has changed since the last calculation, so there is nothing to save or re-run
    const submissionSignature = getScenarioSignature(validEntries);
    if (submissionSignature === lastSubmittedSignature.current) {
      return;
    }
    lastSubmittedSignature.current = submissionSignature;

    // Save the original dictionary to the store
    dispatch(
      projectSlice.actions.updateProjectDraftInternal({
        ...project,
        opmInput: {
          ...project.opmInput,
          exitScenarios: cloneToMutableData(exitScenarios),
        },
      })
    );
    setSubmittedColumnKeys(submittedKeys);
    await dispatch(calculateOpmSampleWaterfall({ validEntries: validEntriesArray }));
  };

  return (
    <div className="main-container-padding-remove">
      <FormProvider {...formMethods}>
        <table
          onBlur={handleSubmit(submitData, submitData)}
          className={`table-primary table-primary--zebra table-primary--framed table-primary--framed-bottom table-primary--title-column-nowrap ${styles['opm-sample-waterfall-table']}`}
          data-testid="opm-sample-waterfall-table">
          <colgroup>
            <col style={{ width: TITLE_WIDTH }} />
            {columnKeys.map((columnKey) => (
              <col key={columnKey} style={{ width: CELL_WIDTH }} />
            ))}
          </colgroup>
          <SampleWaterfallInputs
            yearOptions={yearOptions}
            columnKeys={columnKeys}
            shouldDisplayOutputs={shouldDisplayOutputs}
          />
          {shouldDisplayOutputs && (
            <SampleWaterfallOutputsSummary
              waterfallResults={waterfallResultsByColumn}
              columnKeys={columnKeys}
              exitDatesByColumn={exitDatesByColumn}
            />
          )}
        </table>
      </FormProvider>
    </div>
  );
};

export default SampleWaterfallTable;
